import { z } from 'zod';

import { unauthorizedAxiosInstance } from '@/clients/axios/unauthorized';
import { useMultiAccountStore } from '@/stores/auth/multiAccount';

const workspaceSchema = z.object({
    id: z.string(),
    name: z.string(),
    avatar: z.string().nullish(),
    emoji: z.string().nullish(),
});
const accountSchema = z.object({
    id: z.string(),
    emailAddress: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    plan: z.string().nullish(),
    avatar: z.string().nullish(),
    activeWorkspaceId: z.string().optional(),
    workspaces: z.array(workspaceSchema),
});
export type BrowserAccount = z.infer<typeof accountSchema>;

const headers = { 'X-Countercyclical-Session-Action': '1' };
const options = { headers, withCredentials: true, suppressErrorCapture: true, timeout: 10000 };
const mutationOptions = () => {
    const context = useMultiAccountStore.getState().context;
    return {
        ...options,
        headers: { ...headers, ...(context ? { 'X-Countercyclical-Account-Id': context.accountId } : {}) },
    };
};

/** Serialize cookie mutations across tabs. No credentials are sent through browser messaging or storage. */
export async function withAccountSessionLock<T>(operation: () => Promise<T>): Promise<T> {
    if (typeof navigator !== 'undefined' && navigator.locks) {
        return navigator.locks.request('countercyclical-account-session', operation);
    }
    return operation();
}

export const authSessionService = {
    listAccounts: async () => {
        const response = await unauthorizedAxiosInstance.get('/auth/refresh/accounts', options);
        return z
            .object({
                accounts: z.array(accountSchema),
                activeAccountId: z.string().nullable(),
                maxAccounts: z.number().int().positive(),
                mostRecentWorkspace: z
                    .object({ accountId: z.string(), workspaceId: z.string() })
                    .nullable()
                    .default(null),
            })
            .parse(response.data);
    },
    registerAccount: async () => {
        await unauthorizedAxiosInstance.post('/auth/refresh/register', {}, options);
    },
    addAccount: async () => {
        await unauthorizedAxiosInstance.post('/auth/refresh/add', {}, mutationOptions());
    },
    switchAccount: async (accountId: string, workspaceId?: string) => {
        await unauthorizedAxiosInstance.post('/auth/refresh/switch', { accountId, workspaceId }, mutationOptions());
    },
    logoutAccounts: async (accountIds?: string[]) => {
        await unauthorizedAxiosInstance.post(
            '/auth/refresh/logout',
            accountIds ? { accountIds } : {},
            mutationOptions(),
        );
    },
    refreshAccount: async (accountId?: string) => {
        const response = await unauthorizedAxiosInstance.post('/auth/refresh', accountId ? { accountId } : {}, options);
        return z.object({ accessToken: z.string().min(1), accountId: z.string().min(1) }).parse(response.data);
    },
};
